import { staticFile } from '@common/utils'
import Avatar from '@components/common/avatar'
import AvatarCrop from '@components/student-dashboard/gamification/avatar-crop'
import { FALLBACK_IMAGE } from '@lib/constants/gamification'
import { Trans } from '@lingui/macro'
import { asUploadButton } from '@rpldy/upload-button'
import UploadPreview from '@rpldy/upload-preview'
import Uploady from '@rpldy/uploady'
import getConfig from 'next/config'
import { parseCookies } from 'nookies'
import { useRef, useState } from 'react'
import { Camera } from 'react-feather'

type Props = {
  isCustomAvatar?: boolean
  isInNavbar?: boolean
}

/** i18n support added */
const AvatarUpload = ({
  isCustomAvatar = false,
  isInNavbar = false,
}: Props) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const previewMethodsRef = useRef()

  const getUrl = (url = '/api/v1/users/me/') => {
    const { serverRuntimeConfig, publicRuntimeConfig } = getConfig()

    const endpoint = serverRuntimeConfig.SSR_API_ENDPOINT
      ? serverRuntimeConfig.SSR_API_ENDPOINT
      : publicRuntimeConfig.CSR_API_ENDPOINT

    const finalUrl = url.startsWith('http') ? url : endpoint + url

    return finalUrl
  }

  const getHeader = () => {
    const token = parseCookies().token
    let headers = {}
    if (token) {
      headers = { Authorization: `Token ${token}` }
    }

    return headers
  }

  return (
    <div className="intro-student-profile-avatar">
      <Uploady
        destination={{ url: getUrl(), headers: getHeader() }}
        sendWithFormData={true}
        inputFieldName="avatar"
        method="PATCH"
      >
        <DivUploadButton
          extraProps={{ isCustomAvatar, setShowPreviewModal, isInNavbar }}
        />
        <UploadPreview
          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
          // @ts-ignore
          PreviewComponent={AvatarCrop}
          previewComponentProps={{
            previewMethods: previewMethodsRef,
            isOpen: showPreviewModal,
            setIsOpen: setShowPreviewModal,
          }}
          previewMethodsRef={previewMethodsRef}
          fallbackUrl={staticFile(FALLBACK_IMAGE)}
          imageMimeTypes={['image/jpeg', 'image/webp', 'image/png']}
        />
      </Uploady>
    </div>
  )
}

type DivUploadButtonProps = {
  onClick: () => void
  isCustomAvatar: boolean
  setShowPreviewModal: React.Dispatch<React.SetStateAction<boolean>>
  isInNavbar: boolean
}

/** i18n support added */
const DivUploadButton = asUploadButton(
  ({
    onClick,
    isCustomAvatar,
    setShowPreviewModal,
    isInNavbar,
  }: DivUploadButtonProps) => {
    const handleOnClick = () => {
      setShowPreviewModal(true)
      onClick()
    }

    return (
      <>
        {isInNavbar && (
          <button
            className="block md:w-full md:text-left md:px-4 md:py-2 mt-3 md:mt-0 font-500 md:font-400 text-xl md:text-sm md:leading-5 text-grey md:hover:bg-orange-100 md:focus:outline-none md:focus:bg-orange-100"
            role="menuitem"
            onClick={handleOnClick}
          >
            <Trans>Upload profile picture</Trans>
          </button>
        )}
        {!isInNavbar && !isCustomAvatar && (
          <div
            className="cursor-pointer group hover:underline text-orange font-600 flex flex-col items-center"
            onClick={handleOnClick}
            title="Upload profile picture"
          >
            <button className="focus:outline-none">
              <Camera className="w-16 h-16 lg:w-20 lg:h-20 py-4 cursor-pointer text-grey-600 border-4 border-grey-600 group-hover:text-orange-500 group-hover:border-orange-500 rounded-full" />
            </button>

            <button className="text-orange lg:w-20 font-600 mt-4 focus:outline-none">
              <Trans>Upload profile picture</Trans>
            </button>
          </div>
        )}
        {!isInNavbar && isCustomAvatar && (
          <div className="relative w-32 mx-auto">
            <Avatar size="big" />
            <button
              className="cursor-pointer group focus:outline-none absolute bottom-0 right-0 mr-2 bg-orange group-hover:bg-orange-400 flex items-center justify-center rounded-full h-8 w-8"
              onClick={handleOnClick}
            >
              <Camera
                className={`cursor-pointer p-1 text-white border-grey-600`}
              />
            </button>
          </div>
        )}
      </>
    )
  }
)

export default AvatarUpload
